import React from "react";

export default function PrivacyPolicy() {
  const styles = {
    page: {
      minHeight: "100vh",
      padding: "42px 20px 60px",
      color: "#f7f8ff",
      background:
        "radial-gradient(circle at top left, rgba(139, 92, 246, 0.28), transparent 35%), radial-gradient(circle at top right, rgba(56, 189, 248, 0.18), transparent 35%), linear-gradient(135deg, #070918, #111735)",
      fontFamily: "Arial, Helvetica, sans-serif",
      lineHeight: "1.7",
    },
    wrapper: {
      maxWidth: "1100px",
      margin: "0 auto",
    },
    hero: {
      padding: "34px",
      borderRadius: "28px",
      background:
        "linear-gradient(135deg, rgba(255, 216, 107, 0.16), rgba(139, 92, 246, 0.16)), rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.14)",
      boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
      marginBottom: "24px",
    },
    brand: {
      display: "inline-flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "18px",
      color: "#ffd86b",
      fontWeight: "800",
      letterSpacing: "0.6px",
      textTransform: "uppercase",
      fontSize: "14px",
    },
    badge: {
      width: "42px",
      height: "42px",
      borderRadius: "14px",
      background: "linear-gradient(135deg, #ffd86b, #ffb72c)",
      color: "#1d1730",
      display: "grid",
      placeItems: "center",
      fontSize: "22px",
      boxShadow: "0 10px 30px rgba(255,184,44,0.28)",
    },
    h1: {
      margin: 0,
      fontSize: "clamp(34px, 6vw, 62px)",
      lineHeight: "1.05",
      letterSpacing: "-1.5px",
    },
    subtitle: {
      maxWidth: "820px",
      margin: "18px 0 0",
      color: "#c9cee8",
      fontSize: "18px",
    },
    meta: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      marginTop: "24px",
    },
    pill: {
      padding: "10px 14px",
      borderRadius: "999px",
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "#c9cee8",
      fontSize: "14px",
    },
    section: {
      padding: "26px",
      borderRadius: "24px",
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.14)",
      boxShadow: "0 16px 50px rgba(0,0,0,0.25)",
      marginBottom: "18px",
    },
    h2: {
      margin: "0 0 12px",
      fontSize: "24px",
      color: "#ffd86b",
    },
    p: {
      margin: "0 0 14px",
      color: "#c9cee8",
    },
    ul: {
      margin: "10px 0 0",
      paddingLeft: "22px",
      color: "#c9cee8",
    },
    li: {
      margin: "8px 0",
    },
    highlight: {
      borderLeft: "4px solid #ffd86b",
      padding: "14px 16px",
      borderRadius: "16px",
      background: "rgba(255,216,107,0.08)",
      color: "#f7f8ff",
      marginTop: "12px",
    },
    link: {
      color: "#ffd86b",
      fontWeight: "700",
      textDecoration: "none",
    },
    footer: {
      textAlign: "center",
      color: "#c9cee8",
      padding: "28px 0 0",
      fontSize: "14px",
    },
  };

  return (
    <main style={styles.page}>
      <div style={styles.wrapper}>
        <section style={styles.hero}>
          <div style={styles.brand}>
            <div style={styles.badge}>??</div>
            <span>Puzzlify</span>
          </div>

          <h1 style={styles.h1}>Privacy Policy for Puzzlify</h1>

          <p style={styles.subtitle}>
            This Privacy Policy explains how Puzzlify handles information when you use our mobile puzzle game app.
            Puzzlify is designed as a puzzle and brain-training game collection with local progress, daily challenges,
            hints, rewarded ads, and optional notifications.
          </p>

          <div style={styles.meta}>
            <span style={styles.pill}>Effective date: July 4, 2026</span>
            <span style={styles.pill}>App name: Puzzlify</span>
            <span style={styles.pill}>Website: puzzlify.net</span>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>1. Overview</h2>
          <p style={styles.p}>
            Puzzlify is a mobile puzzle game app that includes games such as Sudoku, Maze, Word Search, Memory,
            Nonogram, Number Slide, Minesweeper, Flow Lines, Number Pyramid, Number Merge, and other logic games.
          </p>
          <p style={styles.p}>
            Puzzlify does not require users to create an account, sign in, or provide personal information such as
            name, email address, phone number, contacts, photos, precise location, camera access, or microphone access.
          </p>
          <div style={styles.highlight}>
            Puzzlify can be used without creating an account. Most game progress is stored locally on your device.
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>2. Information Stored Locally</h2>
          <p style={styles.p}>
            Puzzlify may store gameplay information locally on your device to keep the app working properly and save
            your progress.
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>Completed levels and game progress</li>
            <li style={styles.li}>Coins, stars, hints, and reward progress</li>
            <li style={styles.li}>Daily challenge progress</li>
            <li style={styles.li}>Game statistics and achievements</li>
            <li style={styles.li}>Sound, music, notification, and app preference settings</li>
            <li style={styles.li}>A local player ID or local player profile used only inside the app</li>
          </ul>
          <p style={{ ...styles.p, marginTop: "14px" }}>
            This information is stored on your device and is used for app functionality. If you delete the app, clear
            app data, or change device, this local progress may be lost.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>3. Information We Do Not Directly Collect</h2>
          <p style={styles.p}>Puzzlify does not directly ask you to provide:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Your real name</li>
            <li style={styles.li}>Your email address</li>
            <li style={styles.li}>Your phone number</li>
            <li style={styles.li}>Your contacts</li>
            <li style={styles.li}>Your photos or media files</li>
            <li style={styles.li}>Your precise location</li>
            <li style={styles.li}>Camera or microphone data</li>
            <li style={styles.li}>Account login information</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>4. Advertising and Rewarded Ads</h2>
          <p style={styles.p}>
            Puzzlify uses Google AdMob to show rewarded ads. Rewarded ads are optional ads that players can choose to
            watch in exchange for in-app rewards such as hints.
          </p>
          <p style={styles.p}>
            Ads are not opened automatically for rewards. Players choose when to watch a rewarded ad. Rewards are granted
            only after the rewarded ad is completed successfully.
          </p>
          <p style={styles.p}>
            Google AdMob and the Google Mobile Ads SDK may collect or process certain information to provide ads,
            measure ad performance, prevent fraud, and improve advertising services. This may include:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}>Device identifiers or advertising identifiers</li>
            <li style={styles.li}>IP address</li>
            <li style={styles.li}>App interactions, such as ad views and ad-related activity</li>
            <li style={styles.li}>Diagnostic information, such as app performance and crash-related data</li>
            <li style={styles.li}>Approximate location inferred by Google services, where applicable</li>
          </ul>
          <p style={{ ...styles.p, marginTop: "14px" }}>
            These advertising-related data practices are handled by Google according to Google�s own policies. You can
            learn more here:{" "}
            <a
              style={styles.link}
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Privacy Policy
            </a>
            .
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>5. Notifications</h2>
          <p style={styles.p}>
            Puzzlify may send optional local notifications, such as friendly reminders or daily challenge reminders,
            if notifications are enabled on your device.
          </p>
          <p style={styles.p}>
            On supported Android versions, the app asks for notification permission before sending notifications. You can
            disable notifications at any time from the app settings or from your device system settings.
          </p>
          <p style={styles.p}>
            Notifications are used only to remind you about puzzle challenges or gameplay activity. They are not used to
            collect personal information.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>6. In-App Purchases and Digital Content</h2>
          <p style={styles.p}>
            Puzzlify may offer optional digital content in the future, such as printable puzzle packs or downloadable
            puzzle files. If in-app purchases are added, purchases will be processed through Google Play Billing.
          </p>
          <p style={styles.p}>
            Puzzlify does not directly collect or store your payment card details. Payment processing is handled by
            Google Play according to Google Play�s policies.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>7. Third-Party Services</h2>
          <p style={styles.p}>Puzzlify may use third-party services that help the app work properly, including:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Google AdMob, used to show rewarded ads</li>
            <li style={styles.li}>Google Play Services, used for Android platform features</li>
            <li style={styles.li}>Google Play Billing, if optional purchases are added in the future</li>
            <li style={styles.li}>Android system services, used for local notifications and app storage</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>8. How We Use Information</h2>
          <p style={styles.p}>Information stored or processed by Puzzlify is used for purposes such as:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Saving game progress and app settings</li>
            <li style={styles.li}>Managing hints, coins, stars, and rewards</li>
            <li style={styles.li}>Showing daily challenges and gameplay statistics</li>
            <li style={styles.li}>Sending optional local notifications</li>
            <li style={styles.li}>Showing optional rewarded ads</li>
            <li style={styles.li}>Improving app stability and user experience</li>
            <li style={styles.li}>Supporting future optional purchases, if available</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>9. Data Sharing</h2>
          <p style={styles.p}>Puzzlify does not sell your personal information.</p>
          <p style={styles.p}>
            Some data may be processed by third-party services such as Google AdMob or Google Play Services for
            advertising, analytics, fraud prevention, app functionality, and payment processing where applicable.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>10. Data Security</h2>
          <p style={styles.p}>
            We use reasonable measures to protect app functionality and locally stored gameplay information. However, no
            method of electronic storage or transmission over the internet is completely secure.
          </p>
          <p style={styles.p}>
            Local game progress is stored on your device. You are responsible for maintaining access to your device and
            protecting your device from unauthorized access.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>11. Data Retention and Deletion</h2>
          <p style={styles.p}>
            Since Puzzlify does not currently require account creation, most gameplay data is stored locally on your
            device. You can delete locally stored app data by uninstalling the app or clearing the app data from your
            device settings.
          </p>
          <p style={styles.p}>
            If you contact us by email for support, we may keep your message only as long as necessary to respond to
            your request and improve support.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>12. Children�s Privacy</h2>
          <p style={styles.p}>
            Puzzlify is a general audience puzzle game and is not specifically designed for children under the age of
            13. We do not knowingly collect personal information from children.
          </p>
          <p style={styles.p}>
            If you believe that a child has provided personal information to us, please contact us so we can take
            appropriate action.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>13. Your Choices</h2>
          <p style={styles.p}>You can control your experience in several ways:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Disable notifications from the app settings or Android system settings</li>
            <li style={styles.li}>Reset or delete local progress by clearing app data or uninstalling the app</li>
            <li style={styles.li}>Choose whether to watch rewarded ads</li>
            <li style={styles.li}>Manage advertising preferences using your device or Google account settings</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>14. International Users</h2>
          <p style={styles.p}>
            Puzzlify may be available in different countries. Third-party services such as Google AdMob and Google Play
            may process information according to their own privacy practices and applicable laws.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>15. Changes to This Privacy Policy</h2>
          <p style={styles.p}>
            We may update this Privacy Policy from time to time. When we make changes, we will update the effective date
            at the top of this page. Continued use of Puzzlify after changes means you accept the updated policy.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.h2}>16. Contact Us</h2>
          <p style={styles.p}>
            If you have any questions about this Privacy Policy or Puzzlify�s privacy practices, you can contact us at:
          </p>
          <p style={styles.p}>
            <strong>Email:</strong>{" "}
            <a style={styles.link} href="mailto:3aalam.saghir@gmail.com">
              3aalam.saghir@gmail.com
            </a>
          </p>
          <p style={styles.p}>
            <strong>Website:</strong>{" "}
            <a style={styles.link} href="https://puzzlify.net" target="_blank" rel="noopener noreferrer">
              https://puzzlify.net
            </a>
          </p>
        </section>

        <footer style={styles.footer}>� 2026 Puzzlify. All rights reserved.</footer>
      </div>
    </main>
  );
}